import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { AdminClientFaqSection } from '../components/AdminClientFaqSection.jsx';
import { ClientDocumentsSection } from '../components/ClientDocumentsSection.jsx';
import { FaqTemplatesPanel } from '../components/FaqTemplatesPanel.jsx';
import { ViewAsUserButton } from '../components/ViewAsUserButton.jsx';
import { Modal, ModalConfirmFooter } from '../components/Modal.jsx';
import { clientLabel } from '../utils/clientLabel.js';
import { useAuth } from '../context/AuthContext.jsx';
import { hasPermission } from '../utils/roles.js';
import { TwoFactorUserControls } from '../components/TwoFactorUserControls.jsx';

export function AdminClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageTwoFactor = hasPermission(user, 'two_factor.manage');
  /** @type {[any, Function]} */
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [puName, setPuName] = useState('');
  const [puEmail, setPuEmail] = useState('');
  const [puPassword, setPuPassword] = useState('');
  const [puPassword2, setPuPassword2] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [showCreatePortalUser, setShowCreatePortalUser] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [savingContact, setSavingContact] = useState(false);
  const [contactErrors, setContactErrors] = useState(undefined);

  const resetPortalUserForm = () => {
    setPuName('');
    setPuEmail('');
    setPuPassword('');
    setPuPassword2('');
    setSendInvite(true);
    setFieldErrors(undefined);
  };

  const closeCreatePortalUser = () => {
    setShowCreatePortalUser(false);
    resetPortalUserForm();
  };

  const applyClient = (data) => {
    setClient(data);
    setContactForm({
      name: data.name || '',
      company_name: data.company_name || '',
      email: data.email || '',
      phone: data.phone || '',
      address: data.address || '',
    });
  };

  const load = () =>
    apiJson(`/api/admin/clients/${id}`)
      .then(applyClient)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    void load();
  }, [id]);

  const runSync = async (targets) => {
    setSyncMsg('');
    setError('');
    try {
      const res = await apiJson(`/api/admin/clients/${id}/sync`, {
        method: 'POST',
        body: { targets },
      });
      setSyncMsg(JSON.stringify(res.results, null, 2));
      setClient(res.client);
    } catch (e) {
      setError(e.message);
    }
  };

  const createPortalUser = async (e) => {
    e.preventDefault();
    setFieldErrors(undefined);
    setError('');
    setSubmitting(true);
    try {
      await apiJson(`/api/admin/clients/${id}/portal-users`, {
        method: 'POST',
        body: sendInvite
          ? { name: puName, email: puEmail, send_invite: true }
          : {
              name: puName,
              email: puEmail,
              password: puPassword,
              password_confirmation: puPassword2,
            },
      });
      closeCreatePortalUser();
      setSyncMsg('Portal user created.');
      void load();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSubmitting(false);
    }
  };

  const saveContact = async (e) => {
    e.preventDefault();
    setError('');
    setContactErrors(undefined);
    setSavingContact(true);
    try {
      const updated = await apiJson(`/api/admin/clients/${id}`, {
        method: 'PUT',
        body: {
          name: contactForm.name,
          company_name: contactForm.company_name || null,
          email: contactForm.email || null,
          phone: contactForm.phone || null,
          address: contactForm.address || null,
        },
      });
      setClient((prev) => ({ ...prev, ...updated }));
      setSyncMsg('Contact details saved.');
    } catch (err) {
      setError(err.message);
      setContactErrors(err.body?.errors);
    } finally {
      setSavingContact(false);
    }
  };

  const onDelete = async () => {
    setError('');
    setDeleting(true);
    try {
      await apiJson(`/api/admin/clients/${id}`, { method: 'DELETE' });
      navigate('/admin/clients');
    } catch (err) {
      setError(err.message);
      setShowDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading && !client) {
    return <p className="muted">Loading…</p>;
  }
  if (!client) {
    return <p className="err">{error || 'Not found'}</p>;
  }

  return (
    <div>
      <p>
        <Link to="/admin/clients">← Clients</Link>
      </p>
      <div
        className="row-actions"
        style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}
      >
        <div>
          <h2 style={{ marginTop: 0, marginBottom: 0 }} data-tour="page-title">
            {client.company_name || client.name}
          </h2>
          {client.company_name && client.name && client.company_name !== client.name ? (
            <p className="muted small" style={{ margin: '0.15rem 0 0' }}>{client.name}</p>
          ) : null}
        </div>
        <div className="row-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowDelete(true)}
            data-tour="client-delete"
          >
            Delete client
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowCreatePortalUser(true)}
            data-tour="client-portal-users"
          >
            Add portal user
          </button>
        </div>
      </div>
      {error ? <p className="err">{error}</p> : null}
      <form onSubmit={saveContact} className="auth-card flat stack" data-tour="client-contact">
        <h3 style={{ marginTop: 0 }}>Contact details</h3>
        <label className="field">
          <span>Company</span>
          <input
            value={contactForm.company_name}
            onChange={(ev) => setContactForm((prev) => ({ ...prev, company_name: ev.target.value }))}
          />
        </label>
        <label className="field">
          <span>Name</span>
          <input
            value={contactForm.name}
            onChange={(ev) => setContactForm((prev) => ({ ...prev, name: ev.target.value }))}
            required
          />
        </label>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={contactForm.email}
            onChange={(ev) => setContactForm((prev) => ({ ...prev, email: ev.target.value }))}
          />
        </label>
        <label className="field">
          <span>Phone</span>
          <input
            value={contactForm.phone}
            onChange={(ev) => setContactForm((prev) => ({ ...prev, phone: ev.target.value }))}
          />
        </label>
        <label className="field">
          <span>Address</span>
          <textarea
            value={contactForm.address}
            onChange={(ev) => setContactForm((prev) => ({ ...prev, address: ev.target.value }))}
            rows={3}
          />
        </label>
        <FieldErrors errors={contactErrors} />
        <div>
          <button type="submit" className="btn-primary" disabled={savingContact}>
            {savingContact ? 'Saving…' : 'Save contact details'}
          </button>
        </div>
      </form>
      <div className="auth-card flat" data-tour="client-sync" style={{ marginTop: '1rem' }}>
        {client.ghl_location_name ? (
          <p className="muted small">
            GHL location: {client.ghl_location_name}
            {client.ghl_sync_enabled ? (
              <span className="badge badge-ok" style={{ marginLeft: '0.5rem' }}>sync on</span>
            ) : (
              <span className="badge badge-warn" style={{ marginLeft: '0.5rem' }}>sync off</span>
            )}
            {' · '}
            <Link to="/admin/ghl-locations">Configure</Link>
          </p>
        ) : (
          <p className="muted small">
            GHL location not linked. <Link to="/admin/ghl-locations">Link in GHL Locations</Link>
          </p>
        )}
        <p className="muted small">GHL contact: {client.ghl_contact_id || '—'}</p>
        <p className="muted small">Bitwarden collection: {client.bitwarden_collection_id || '—'}</p>
        <p className="muted small">
          Teams / SharePoint folder:{' '}
          {client.teams_folder_web_url ? (
            <a href={client.teams_folder_web_url} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            '—'
          )}
        </p>
        <div className="row-actions" style={{ marginTop: '0.75rem' }}>
          <button type="button" className="btn-secondary" onClick={() => void runSync(['ghl'])}>
            Sync GHL
          </button>
          <button type="button" className="btn-secondary" onClick={() => void runSync(['bitwarden'])}>
            Sync Bitwarden
          </button>
          <button type="button" className="btn-secondary" onClick={() => void runSync(['teams'])}>
            Sync Teams folder
          </button>
          <button type="button" className="btn-primary" onClick={() => void runSync(['ghl', 'bitwarden', 'teams'])}>
            Sync all
          </button>
        </div>
        {syncMsg ? (
          <pre className="sync-pre" style={{ marginTop: '1rem' }}>
            {syncMsg}
          </pre>
        ) : null}
      </div>

      {(client.portal_users?.length ?? 0) > 0 ? (
        <div className="auth-card flat" style={{ marginTop: '1rem' }}>
          <h3>Portal users</h3>
          <ul className="list-plain">
            {client.portal_users.map((pu) => (
              <li
                key={pu.id}
                className="row-actions"
                style={{ justifyContent: 'space-between', marginBottom: '0.5rem' }}
              >
                <span>
                  {pu.name} · {pu.email}
                  {pu.pending_invite ? ' (invite pending)' : ''}
                  {pu.is_active === false ? ' (inactive)' : ''}
                </span>
                <span className="row-actions">
                  {pu.can_impersonate ? <ViewAsUserButton userId={pu.id} label="View as client" /> : null}
                  {canManageTwoFactor ? (
                    <TwoFactorUserControls
                      userId={pu.id}
                      enabled={Boolean(pu.two_factor_enabled)}
                      enforced={Boolean(pu.two_factor_enforced)}
                      onChanged={load}
                    />
                  ) : null}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={async () => {
                      await apiJson(`/api/admin/clients/${id}/portal-users/${pu.id}`, {
                        method: 'PUT',
                        body: { is_active: !pu.is_active },
                      });
                      void load();
                    }}
                  >
                    {pu.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={async () => {
                      await apiJson(`/api/admin/clients/${id}/portal-users/${pu.id}/password-reset`, {
                        method: 'POST',
                      });
                      setSyncMsg('Password setup link sent.');
                    }}
                  >
                    Send password link
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Modal open={showCreatePortalUser} title="Portal login for this client" onClose={closeCreatePortalUser}>
        <form onSubmit={createPortalUser} className="stack">
          <label className="field">
            <span>Name</span>
            <input value={puName} onChange={(ev) => setPuName(ev.target.value)} required />
          </label>
          <label className="field">
            <span>Email</span>
            <input type="email" value={puEmail} onChange={(ev) => setPuEmail(ev.target.value)} required />
          </label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={sendInvite} onChange={(ev) => setSendInvite(ev.target.checked)} />
            <span>Send a link so they can create their own password</span>
          </label>
          {!sendInvite ? (
            <>
              <label className="field">
                <span>Password</span>
                <input type="password" value={puPassword} onChange={(ev) => setPuPassword(ev.target.value)} required />
              </label>
              <label className="field">
                <span>Confirm password</span>
                <input
                  type="password"
                  value={puPassword2}
                  onChange={(ev) => setPuPassword2(ev.target.value)}
                  required
                />
              </label>
            </>
          ) : null}
          <FieldErrors errors={fieldErrors} />
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary" onClick={closeCreatePortalUser} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create portal user'}
            </button>
          </div>
        </form>
      </Modal>

      <div className="auth-card flat" style={{ marginTop: '1rem' }}>
        <h3>Tools & documents</h3>
        <p className="muted small">Shared links visible in the client portal under Tools & Links.</p>
        <ClientDocumentsSection clientId={id} />
      </div>

      <FaqTemplatesPanel clientId={id} />

      <div className="auth-card flat" style={{ marginTop: '1rem' }} data-tour="client-faq">
        <h3>Forms & questionnaires</h3>
        <p className="muted small">
          Build the client&apos;s FAQ hub: answers, credentials, files, and intake forms. Available features depend on
          your subscription plan.
        </p>
        <AdminClientFaqSection clientId={id} />
      </div>

      <div data-tour="client-projects">
      <h3>Projects</h3>
      <ul>
        {(client.projects || []).map((p) => (
          <li key={p.id}>
            <Link to={`/admin/projects/${p.id}`}>{p.name}</Link> · {p.type} · {p.status}
          </li>
        ))}
      </ul>
      <p>
        <Link to={`/admin/projects?client_id=${client.id}`}>Create project for this client</Link>
      </p>
      </div>

      <Modal
        open={showDelete}
        title={`Delete “${clientLabel(client)}”?`}
        description={`This permanently removes the client and its ${client.projects?.length ?? 0} project(s). Portal users are unlinked, not deleted.`}
        onClose={() => {
          if (!deleting) {
            setShowDelete(false);
          }
        }}
        footer={
          <ModalConfirmFooter
            danger
            confirmLabel="Delete"
            confirmBusy={deleting}
            onCancel={() => setShowDelete(false)}
            onConfirm={() => void onDelete()}
          />
        }
      >
        <p className="muted small" style={{ margin: 0 }}>
          This action is permanent.
        </p>
      </Modal>
    </div>
  );
}
